import { format, intlFormat } from 'date-fns';
import Fuse from 'fuse.js';
import { type TaskTimeDuration } from '@/model/Task.model';
import { DateUtils } from '@/utils/Date.utils';

type FromOption = { label: string; offset: number; search: string };
type ToOption = FromOption & { duration: number; durationInHours: string; isInvalid?: boolean };
export type DurationPickerState = {
  allDay: boolean;
  from: number;
  duration: number;
  isCrossMidnight: boolean;
};

export const minimumDuration = 30;
export const minutesInDay = 24 * 60;

export const initialState = (value: TaskTimeDuration): DurationPickerState => {
  if (value.type === 'all_day') {
    return { allDay: true, from: -1, duration: minimumDuration, isCrossMidnight: false };
  }
  const fromDate = DateUtils.isoTimeToDate(value.from);
  const toDate = DateUtils.isoTimeToDate(value.to);
  const from = fromDate.getHours() * 60 + fromDate.getMinutes();
  let to = toDate.getHours() * 60 + toDate.getMinutes();

  // Treat midnight (00:00) as end of day (24:00 = 1440 minutes)
  if (to === 0) {
    to = minutesInDay;
  }

  const duration = to - from;
  const isCrossMidnight = duration < 0;
  const calculatedDuration = isCrossMidnight ? duration + minutesInDay : duration;

  // Auto-convert to all-day if duration is exactly 24 hours (1440 minutes)
  if (calculatedDuration === minutesInDay && !isCrossMidnight) {
    return { allDay: true, from: -1, duration: minimumDuration, isCrossMidnight: false };
  }

  return { allDay: false, from, duration: calculatedDuration, isCrossMidnight };
};
const offsetToDate = (offset: number): Date => new Date(0, 0, 0, 0, offset);
const formatFromOptionOffset = (offset: Date): Pick<FromOption, 'label' | 'search'> => {
  return {
    label: intlFormat(offset, { hour: 'numeric', minute: '2-digit' }),
    search: format(offset, 'HH haa bb'),
  };
};
const formatDuration = (duration: number): string => {
  const hours = duration / 60;
  return `${hours % 1 === 0 ? Math.floor(hours) : hours.toFixed(1)}hr`;
};
const formatToOptionOffset = (
  offset: Date,
  duration: number
): Pick<ToOption, 'label' | 'search' | 'durationInHours'> => {
  const { label, search } = formatFromOptionOffset(offset);
  const formattedDuration = formatDuration(duration);
  return {
    label: `${label} (${formattedDuration})`,
    search,
    durationInHours: formattedDuration,
  };
};

export const fromOptionData = () => {
  const fromOptionsMap = Array.from({ length: minutesInDay / minimumDuration }).reduce<Map<number, FromOption>>(
    (acc, _, index) => {
      const offset = index * minimumDuration;
      return acc.set(offset, { offset, ...formatFromOptionOffset(offsetToDate(offset)) });
    },
    new Map<number, FromOption>()
  );
  const fromOptions = [...fromOptionsMap.values()].sort((left, right) => left.offset - right.offset);
  const fromOptionsFuse = new Fuse(fromOptions, {
    includeScore: false,
    threshold: 0.15,
    ignoreLocation: true,
    keys: [{ name: ['label'] }, { name: ['search'] }],
  });

  return {
    fromOptionsMap,
    fromOptions,
    fromOptionsFuse,
  };
};
export const allDayToOption: ToOption = {
  duration: minutesInDay,
  offset: minutesInDay,
  ...formatToOptionOffset(offsetToDate(minutesInDay), minutesInDay),
};

export const toOptionData = (fromOffset: number, crossMidnightDuration?: number) => {
  const toOptionsMap = new Map<number, ToOption>();
  for (let offset = fromOffset + minimumDuration; offset <= minutesInDay; offset += minimumDuration) {
    const duration = offset - fromOffset;

    // Handle midnight (offset === minutesInDay) as a special case
    const displayOffset = offset === minutesInDay ? 0 : offset;

    toOptionsMap.set(duration, {
      duration,
      offset,
      ...formatToOptionOffset(offsetToDate(displayOffset), duration),
    });
  }

  // Add cross-midnight option if provided
  if (crossMidnightDuration !== undefined) {
    const crossMidnightOffset = fromOffset + crossMidnightDuration;
    toOptionsMap.set(crossMidnightDuration, {
      duration: crossMidnightDuration,
      offset: crossMidnightOffset,
      isInvalid: true,
      ...formatToOptionOffset(offsetToDate(crossMidnightOffset), crossMidnightDuration),
      label: `${formatToOptionOffset(offsetToDate(crossMidnightOffset), crossMidnightDuration).label} (extends past midnight)`,
    });
  }
  const toOptions = [...toOptionsMap.values()].sort((left, right) => left.duration - right.duration);
  const toOptionsFuse = new Fuse(toOptions, {
    includeScore: false,
    threshold: 0.15,
    ignoreLocation: true,
    keys: [{ name: ['label'] }, { name: ['search'] }, { name: ['durationInHours'] }],
  });
  return {
    toOptionsMap,
    toOptions,
    toOptionsFuse,
  };
};
export const toggleAllDayTask = (prev: DurationPickerState): DurationPickerState => {
  const now = new Date();
  if (prev.from < 0) {
    const from = Math.ceil((now.getHours() * 60 + now.getMinutes()) / 30) * 30;
    return from + prev.duration > minutesInDay
      ? { ...prev, allDay: !prev.allDay, from: minutesInDay - prev.duration, isCrossMidnight: false }
      : { ...prev, allDay: !prev.allDay, from, isCrossMidnight: false };
  }
  return { ...prev, allDay: !prev.allDay, isCrossMidnight: false };
};

export const stateToDurationValue = (state: DurationPickerState): TaskTimeDuration => {
  if (state.allDay) {
    return { type: 'all_day' };
  }
  const from = format(offsetToDate(state.from), 'HH:mm');
  const to = format(offsetToDate(state.from + state.duration), 'HH:mm');
  return { type: 'timed', from, to };
};
